/**
 * Stat tile: a hero number, an optional signed delta, an optional sparkline.
 *
 * A bare stat tile is the one shape the standard exempts from the hover layer —
 * there is nothing to hover, the number is already the value. The delta is the
 * interesting part: it is a STATUS, so it is drawn with an arrow glyph and a
 * word as well as a colour. Colour alone is not a signal.
 *
 * `deltaGood` exists because "up" is not universally good. For error rate,
 * latency or cost, down is the win; a tile that paints every rise green is
 * actively misleading.
 */

import { formatTick } from "./charts-scale.mjs";
import { statusColor } from "./charts-palette.mjs";
import { Chart } from "./chart";
import { ensureChartCss } from "./styles";
import { resolveChartTheme } from "./theme";
import type { ChartHandle, ChartMode, ChartOptions, ChartTable } from "./types";

export interface StatTileHandle extends ChartHandle {
  setValue(value: number | string, delta?: number): void;
}

export function createStatTile(
  options: ChartOptions,
  subscribeTheme?: (fn: () => void) => () => void
): StatTileHandle {
  ensureChartCss();
  const stat = options.stat || { value: "—" };

  const el = document.createElement("div");
  el.className = "os-stat";

  const label = document.createElement("div");
  label.className = "os-stat-label";
  const row = document.createElement("div");
  row.className = "os-stat-row";
  const value = document.createElement("div");
  value.className = "os-stat-value";
  const delta = document.createElement("span");
  delta.className = "os-stat-delta";
  row.append(value, delta);
  el.append(label, row);

  let spark: Chart | null = null;
  if (stat.sparkline && stat.sparkline.length) {
    const host = document.createElement("div");
    host.className = "os-stat-spark";
    el.appendChild(host);
    spark = new Chart(
      {
        host,
        kind: "sparkline",
        interactive: false,
        series: [{ key: options.title || "trend", label: options.title || "Trend", data: [...stat.sparkline] }],
      },
      subscribeTheme
    );
  }

  let current = { ...stat };
  let theme = resolveChartTheme(el, options.theme || "auto");

  const fmt = (v: number | string): string => {
    if (typeof v !== "number") return String(v);
    return current.format ? current.format(v) : formatTick(v, 1);
  };

  function paint() {
    theme = resolveChartTheme(el, options.theme || "auto");
    label.textContent = current.label || options.title || "";
    label.hidden = !label.textContent;
    value.textContent = fmt(current.value);

    const d = current.delta;
    if (typeof d !== "number" || !Number.isFinite(d)) {
      delta.hidden = true;
      return;
    }
    delta.hidden = false;
    const goodWhenUp = current.deltaGood !== "down";
    const isGood = d === 0 ? null : d > 0 === goodWhenUp;
    // Glyph + word carry the meaning; colour is the third, redundant channel.
    const glyph = d > 0 ? "▲" : d < 0 ? "▼" : "■";
    const word = current.deltaLabel || (d > 0 ? "up" : d < 0 ? "down" : "flat");
    const text = `${fmt(Math.abs(d))} ${word}`;
    delta.replaceChildren();
    const g = document.createElement("span");
    g.className = "os-stat-deltaglyph";
    g.textContent = glyph;
    const t = document.createElement("span");
    t.textContent = text;
    delta.append(g, t);
    delta.setAttribute("aria-label", `${word} ${fmt(Math.abs(d))}`);
    delta.style.color =
      isGood === null
        ? theme.textDim
        : statusColor(isGood ? "good" : "serious", { label: word, mode: theme.mode });
  }

  paint();
  if (options.host) options.host.appendChild(el);

  const unsubscribe = subscribeTheme ? subscribeTheme(() => paint()) : null;

  const table = (): ChartTable => {
    const columns = [current.label || options.title || "Metric", "Value"];
    const rows: Array<Array<string | number | null>> = [
      ["Value", typeof current.value === "number" ? current.value : String(current.value)],
    ];
    if (typeof current.delta === "number") rows.push(["Change", current.delta]);
    (current.sparkline || []).forEach((v, i) => rows.push([`t${i}`, v ?? null]));

    const t = document.createElement("table");
    t.className = "os-chart-table";
    const tb = document.createElement("tbody");
    for (const r of rows) {
      const tr = document.createElement("tr");
      const th = document.createElement("th");
      th.scope = "row";
      th.textContent = String(r[0]);
      const td = document.createElement("td");
      td.textContent = r[1] == null ? "—" : fmt(r[1] as number);
      tr.append(th, td);
      tb.appendChild(tr);
    }
    t.appendChild(tb);
    const wrap = document.createElement("div");
    wrap.className = "os-chart-tablescroll";
    wrap.appendChild(t);

    return {
      columns,
      rows,
      el: wrap,
      toCsv: () =>
        [columns.join(","), ...rows.map((r) => r.map((c) => (c == null ? "" : c)).join(","))].join(
          "\r\n"
        ),
    };
  };

  return {
    el,
    setValue(v, d) {
      current = { ...current, value: v, delta: d };
      paint();
    },
    setData(data) {
      const list = Array.isArray(data) ? data[0] : Object.values(data)[0];
      const nums = (list || []).map((p) =>
        typeof p === "number" ? p : p && typeof p === "object" ? p.y : null
      );
      current = { ...current, sparkline: nums };
      spark?.setData([nums]);
      paint();
    },
    setSeries(series) {
      spark?.setSeries(series);
    },
    setTitle(title) {
      current = { ...current, label: title };
      paint();
    },
    setCategories() {
      /* a stat tile has no category axis */
    },
    resize() {
      spark?.resize();
    },
    toTable: table,
    setTableVisible() {
      /* the tile has no table panel of its own; use toTable() */
    },
    getMode: (): ChartMode => theme.mode,
    destroy() {
      unsubscribe?.();
      spark?.destroy();
      el.remove();
    },
  };
}
